import { useRef, type ChangeEvent, type UIEvent } from 'react';
import { Textarea } from '@skin-seoul/design-system';
import { toFlaggedTextSegments } from '@skin-seoul/service';

interface ReviewBodyTextareaProps {
  content: string;
  setContent: (content: string) => void;
  flaggedText?: string[];
}

/**
 * A textarea cannot colour part of its own value, so the marks are painted on a copy behind it.
 * Both layers must stay pixel-identical or the marks drift off the words — hence the shared class.
 */
const LAYER_CLASS = 'w-full min-h-20 px-3 py-2 text-sm rounded-lg border whitespace-pre-wrap break-words';

export const ReviewBodyTextarea = ({ content, setContent, flaggedText }: ReviewBodyTextareaProps) => {
  const backdropRef = useRef<HTMLDivElement>(null);
  const segments = toFlaggedTextSegments(content, flaggedText);
  const hasMarks = segments.some((segment) => segment.isFlagged);

  // A backdrop that stays put would leave the marks on the wrong lines.
  const handleScroll = (event: UIEvent<HTMLTextAreaElement>) => {
    const backdrop = backdropRef.current;
    if (!backdrop) {
      return;
    }
    backdrop.scrollTop = event.currentTarget.scrollTop;
    backdrop.scrollLeft = event.currentTarget.scrollLeft;
  };

  return (
    <div className="relative w-full">
      {/* Decorative: the text it repeats is already in the textarea, and reading it twice would make
          the field sound duplicated to a screen reader. */}
      {hasMarks && (
        <div
          ref={backdropRef}
          aria-hidden="true"
          data-testid="review-body-highlights"
          className={`${LAYER_CLASS} absolute inset-0 overflow-hidden border-transparent text-transparent select-none pointer-events-none`}
        >
          {segments.map((segment, index) =>
            segment.isFlagged ? (
              // Vertical only: padding an inline box sideways shifts the text after it.
              <mark key={index} className="bg-error-75 text-transparent rounded-[4px] py-[3px]">
                {segment.text}
              </mark>
            ) : (
              <span key={index}>{segment.text}</span>
            ),
          )}
        </div>
      )}
      <Textarea
        id="review-content"
        value={content}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setContent(event.target.value)}
        onScroll={handleScroll}
        // Transparent so the marks show through; `relative` keeps the caret above them.
        className={`${LAYER_CLASS} relative bg-transparent text-neutral-700 resize-y`}
      />
    </div>
  );
};
